import {
  ACK_NOTE_MAX,
  ACK_REASONS,
  type AckReason,
  type ChangeAck,
  type RunEvent,
} from "@checkone/contract/client";
import { useEffect, useId, useRef, useState } from "react";
import { useApiMutation } from "../../api/hooks";
import { useToast } from "../../components/Toasts";
import { formatDateTime } from "../../lib/format";

/**
 * The customer's own record of what they did about a change. The wording follows the change: a vendor that
 * dropped off the income-tax ATL is moved to the non-ATL rate, one that re-entered goes back to the ATL rate.
 */
export function reasonLabel(reason: AckReason, e?: Pick<RunEvent, "source" | "change">): string {
  switch (reason) {
    case "switched_rate":
      if (e?.source === "ATL_ST") return "Changed how we pay this vendor";
      return e?.change === "added" ? "Switched back to the ATL rate" : "Switched to the non-ATL rate";
    case "payment_held":
      return "Payment held";
    case "vendor_contacted":
      return "Vendor contacted";
    case "no_payment_due":
      return "No payment due";
    case "other":
      return "Other";
  }
}

function who(a: ChangeAck): string {
  return a.by.name?.trim() || a.by.email;
}

/** The current mark (the newest), who made it and when; earlier marks are corrections and are listed. */
function AckSummary({ event }: { event: RunEvent }) {
  const [latest, ...earlier] = event.acks;
  if (!latest) return <span className="pill warn">Not marked actioned</span>;
  return (
    <div>
      <span className="pill ok">{reasonLabel(latest.reason, event)}</span>
      <div className="subtle">
        {who(latest)}, {formatDateTime(latest.at)}
      </div>
      {latest.note && <div className="text-2">{latest.note}</div>}
      {earlier.length > 0 && (
        <details className="subtle">
          <summary>
            {earlier.length} earlier {earlier.length === 1 ? "mark" : "marks"}
          </summary>
          <ul style={{ margin: 0, paddingLeft: "1rem" }}>
            {earlier.map((a) => (
              <li key={a.id}>
                {reasonLabel(a.reason, event)} · {who(a)}, {formatDateTime(a.at)}
                {a.note && <> · {a.note}</>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * "Mark actioned" for one status change: a reason from a short list and an optional note (needed for
 * "Other"). Insert-only on the server, so changing your mind adds a correction above the earlier mark.
 * Viewers see the trail but not the control.
 *
 * Focus (WCAG 2.4.3): opening the form moves focus to the reason; Cancel or Save brings it back to the
 * button that opened it. A list that drops the row once it is marked is told through `onMarked` and moves
 * focus itself, since this cell is about to go.
 */
export function ActionedCell({
  event,
  canMark,
  onMarked,
}: {
  event: RunEvent;
  canMark: boolean;
  onMarked?: (eventId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<AckReason | "">("");
  const [note, setNote] = useState("");
  const mark = useApiMutation("changes.ack", ["runs.events", "changes.unactioned"]);
  const toast = useToast();
  const id = useId();
  const needsNote = reason === "other" && note.trim() === "";
  const toggleRef = useRef<HTMLButtonElement>(null);
  const reasonRef = useRef<HTMLSelectElement>(null);
  const returnFocus = useRef(false);
  useEffect(() => {
    if (open) reasonRef.current?.focus();
    else if (returnFocus.current) {
      returnFocus.current = false;
      toggleRef.current?.focus();
    }
  }, [open]);
  const close = () => {
    returnFocus.current = true;
    setOpen(false);
  };

  return (
    <div className="stack" style={{ gap: "var(--space-1)" }}>
      <AckSummary event={event} />
      {canMark && !open && (
        <button
          ref={toggleRef}
          type="button"
          className="btn small"
          aria-label={`${event.acks.length ? "Correct the mark" : "Mark actioned"} for ${event.vendor_name}`}
          onClick={() => setOpen(true)}
        >
          {event.acks.length ? "Correct" : "Mark actioned"}
        </button>
      )}
      {canMark && open && (
        <form
          className="stack"
          style={{ gap: "var(--space-1)" }}
          aria-label={`Mark the change for ${event.vendor_name} actioned`}
          onSubmit={(e) => {
            e.preventDefault();
            if (!reason || needsNote) return;
            mark.mutate(
              {
                params: { id: event.id },
                body: { reason, ...(note.trim() ? { note: note.trim() } : {}) },
              },
              {
                onSuccess: () => {
                  toast.success(`Marked actioned: ${event.vendor_name}`);
                  close();
                  setReason("");
                  setNote("");
                  onMarked?.(event.id);
                },
                onError: (err) => toast.error(err),
              },
            );
          }}
        >
          <label htmlFor={`${id}-reason`} className="label">
            What did you do?
          </label>
          <select
            ref={reasonRef}
            id={`${id}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value as AckReason | "")}
            required
          >
            <option value="">Choose…</option>
            {ACK_REASONS.map((r) => (
              <option key={r} value={r}>
                {reasonLabel(r, event)}
              </option>
            ))}
          </select>
          <label htmlFor={`${id}-note`} className="label">
            Note {reason === "other" ? "(needed for Other)" : "(optional)"}
          </label>
          <input
            id={`${id}-note`}
            type="text"
            maxLength={ACK_NOTE_MAX}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. payment voucher number"
          />
          <div className="row" style={{ gap: "var(--space-2)" }}>
            <button
              type="submit"
              className="btn small primary"
              disabled={!reason || needsNote || mark.isPending}
            >
              {mark.isPending ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn small" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
